import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OfficeHoursPanel } from "@/components/admin/office-hours-panel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  MessageSquare,
  Check,
  AlertCircle,
  Loader2,
  Plus,
  Trash2,
  Copy,
  Link2,
  Shield,
  User,
  Send,
  Settings2,
  Clock,
  Eye,
  EyeOff,
} from "lucide-react";
import type { SmsProviderConfig, SmsUserPermission, SmsConnectionInvite, UserProfile } from "@shared/schema";
import type { User as UserType } from "@shared/models/auth";

interface UserWithProfile extends UserType {
  profile?: UserProfile;
}

interface CommunicationsSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Allow callers to deep-link straight to a tab. (Cold sending-domain
  // setup moved to the Email panel's Cold Email area — Task #392.)
  initialTab?: "provider" | "permissions" | "invites" | "office-hours";
}

export function CommunicationsSettingsDialog({ open, onOpenChange, initialTab }: CommunicationsSettingsDialogProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState(initialTab ?? "provider");
  // Re-apply initialTab whenever the dialog is (re)opened so a fresh
  // deep-link from a different surface lands on the right tab.
  useEffect(() => {
    if (open && initialTab) setActiveTab(initialTab);
  }, [open, initialTab]);
  const [showAuthToken, setShowAuthToken] = useState(false);
  
  // SMS Provider form state
  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [fromNumber, setFromNumber] = useState("");
  const [providerName, setProviderName] = useState("Primary Twilio");
  
  // Invite form state
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedPermUserId, setSelectedPermUserId] = useState<string>("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [expiryDays, setExpiryDays] = useState("7");

  // Fetch SMS providers
  const { data: providers = [], isLoading: loadingProviders } = useQuery<SmsProviderConfig[]>({
    queryKey: ["/api/sms-admin/providers"],
    enabled: open,
  });

  // Fetch SMS permissions
  const { data: permissions = [], isLoading: loadingPermissions } = useQuery<SmsUserPermission[]>({
    queryKey: ["/api/sms-admin/permissions"],
    enabled: open,
  });

  // Fetch SMS invites
  const { data: invites = [], isLoading: loadingInvites } = useQuery<SmsConnectionInvite[]>({
    queryKey: ["/api/sms-admin/invites"],
    enabled: open,
  });

  // Fetch all users for permission assignment
  const { data: users = [] } = useQuery<UserWithProfile[]>({
    queryKey: ["/api/users"],
    enabled: open,
  });

  // Mutations
  const createProviderMutation = useMutation({
    mutationFn: async (data: { providerName: string; accountSid: string; authToken: string; fromNumber: string }) => {
      const res = await apiRequest("POST", "/api/sms-admin/providers", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-admin/providers"] });
      toast({ title: "Provider Added", description: "SMS provider configuration saved successfully." });
      setAccountSid("");
      setAuthToken("");
      setFromNumber("");
      setProviderName("Primary Twilio");
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to save provider configuration.", variant: "destructive" });
    },
  });

  const deleteProviderMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/sms-admin/providers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-admin/providers"] });
      toast({ title: "Provider Removed", description: "SMS provider configuration deleted." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete provider.", variant: "destructive" });
    },
  });

  const setPrimaryMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", `/api/sms-admin/providers/${id}/set-primary`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-admin/providers"] });
      toast({ title: "Primary Set", description: "Provider set as primary for sending campaigns." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to set primary provider.", variant: "destructive" });
    },
  });

  const grantPermissionMutation = useMutation({
    mutationFn: async (data: { userId: string; permissionType: string }) => {
      const res = await apiRequest("POST", "/api/sms-admin/permissions", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-admin/permissions"] });
      toast({ title: "Permission Granted", description: "SMS permission added successfully." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to grant permission.", variant: "destructive" });
    },
  });

  const revokePermissionMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/sms-admin/permissions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-admin/permissions"] });
      toast({ title: "Permission Revoked", description: "SMS permission removed." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to revoke permission.", variant: "destructive" });
    },
  });

  const createInviteMutation = useMutation({
    mutationFn: async (data: { userId?: string; email?: string; expiresInDays: number }) => {
      const res = await apiRequest("POST", "/api/sms-admin/invites", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-admin/invites"] });
      toast({ title: "Invite Created", description: "Connection invite link created." });
      setSelectedUserId("");
      setInviteEmail("");
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create invite.", variant: "destructive" });
    },
  });

  const revokeInviteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/sms-admin/invites/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sms-admin/invites"] });
      toast({ title: "Invite Revoked", description: "Connection invite removed." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to revoke invite.", variant: "destructive" });
    },
  });

  const copyInviteLink = (token: string) => {
    const link = `${window.location.origin}/connect-sms/${token}`;
    navigator.clipboard.writeText(link);
    toast({ title: "Link Copied", description: "Invite link copied to clipboard." });
  };

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
      return name || user.email || userId;
    }
    return userId;
  };

  const primaryProvider = providers.find(p => p.isPrimary);
  const hasPrimaryProvider = !!primaryProvider;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Communications Settings
          </DialogTitle>
          <DialogDescription>
            Configure SMS providers, user permissions, and connection invites
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="provider" className="flex items-center gap-2" data-testid="tab-provider">
              <Settings2 className="w-4 h-4" />
              Provider
            </TabsTrigger>
            <TabsTrigger value="permissions" className="flex items-center gap-2" data-testid="tab-permissions">
              <Shield className="w-4 h-4" />
              Permissions
            </TabsTrigger>
            <TabsTrigger value="invites" className="flex items-center gap-2" data-testid="tab-invites">
              <Link2 className="w-4 h-4" />
              Invites
            </TabsTrigger>
            <TabsTrigger value="office-hours" className="flex items-center gap-2" data-testid="tab-office-hours">
              <Clock className="w-4 h-4" />
              Office Hours
            </TabsTrigger>
          </TabsList>

          <TabsContent value="provider" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  SMS Provider Status
                </CardTitle>
                <CardDescription>
                  {hasPrimaryProvider 
                    ? "SMS campaigns will use the primary provider configuration below."
                    : "No SMS provider configured. SMS campaigns will run in dry-run mode."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {hasPrimaryProvider ? (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                      <Check className="w-3 h-3 mr-1" />
                      Active
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {primaryProvider.name} ({primaryProvider.fromNumber})
                    </span>
                  </div>
                ) : (
                  <Badge variant="outline" className="text-amber-600 border-amber-500/20">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Not Configured
                  </Badge>
                )}
              </CardContent>
            </Card>

            {/* Existing Providers */}
            {providers.length > 0 && (
              <div className="space-y-2">
                <Label>Configured Providers</Label>
                {providers.map((provider) => (
                  <Card key={provider.id} className={provider.isPrimary ? "border-green-500/30" : ""}>
                    <CardContent className="py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <MessageSquare className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm flex items-center gap-2">
                            {provider.name}
                            {provider.isPrimary && (
                              <Badge className="text-xs bg-green-500/10 text-green-600">Primary</Badge>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            From: {provider.fromNumber} | SID: {provider.accountSid?.slice(0, 8)}...
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!provider.isPrimary && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setPrimaryMutation.mutate(provider.id)}
                            disabled={setPrimaryMutation.isPending}
                            data-testid={`button-set-primary-${provider.id}`}
                          >
                            Set Primary
                          </Button>
                        )}
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="text-destructive hover:text-destructive"
                          onClick={() => deleteProviderMutation.mutate(provider.id)}
                          disabled={deleteProviderMutation.isPending}
                          data-testid={`button-delete-provider-${provider.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Add New Provider */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Add SMS Provider (Twilio)</CardTitle>
                <CardDescription>Enter your Twilio credentials to enable SMS campaigns</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="providerName">Provider Name</Label>
                  <Input
                    id="providerName"
                    placeholder="e.g., Primary Twilio"
                    value={providerName}
                    onChange={(e) => setProviderName(e.target.value)}
                    data-testid="input-provider-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accountSid">Account SID</Label>
                  <Input
                    id="accountSid"
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    value={accountSid}
                    onChange={(e) => setAccountSid(e.target.value)}
                    data-testid="input-account-sid"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="authToken">Auth Token</Label>
                  <div className="relative">
                    <Input
                      id="authToken"
                      type={showAuthToken ? "text" : "password"}
                      placeholder="Your Twilio Auth Token"
                      value={authToken}
                      onChange={(e) => setAuthToken(e.target.value)}
                      data-testid="input-auth-token"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowAuthToken(!showAuthToken)}
                    >
                      {showAuthToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fromNumber">From Phone Number</Label>
                  <Input
                    id="fromNumber"
                    placeholder="+1XXXXXXXXXX"
                    value={fromNumber}
                    onChange={(e) => setFromNumber(e.target.value)}
                    data-testid="input-from-number"
                  />
                  <p className="text-xs text-muted-foreground">Must be a valid Twilio phone number in E.164 format</p>
                </div>
                <Button
                  onClick={() => createProviderMutation.mutate({ providerName, accountSid, authToken, fromNumber })}
                  disabled={!accountSid || !authToken || !fromNumber || createProviderMutation.isPending}
                  className="w-full"
                  data-testid="button-add-provider"
                >
                  {createProviderMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Provider
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="permissions" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">SMS Permissions</CardTitle>
                <CardDescription>
                  Control which users can send SMS, create campaigns, or use SMS in automations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Permissions */}
                {loadingPermissions ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : permissions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No permissions granted yet. Add users below to enable their SMS access.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {permissions.map((perm) => (
                      <div key={perm.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium text-sm">{getUserName(perm.userId)}</p>
                            <Badge variant="outline" className="text-xs mt-1">
                              {perm.permissionType.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => revokePermissionMutation.mutate(perm.id)}
                          disabled={revokePermissionMutation.isPending}
                          data-testid={`button-revoke-permission-${perm.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <Separator />

                {/* Grant New Permission */}
                <div className="space-y-3">
                  <Label>Grant Permission</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={selectedPermUserId} onValueChange={setSelectedPermUserId}>
                      <SelectTrigger data-testid="select-permission-user">
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => u.profile?.role !== 'pending').map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || user.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => grantPermissionMutation.mutate({ userId: selectedPermUserId, permissionType: 'can_send_sms' })}
                        disabled={!selectedPermUserId || grantPermissionMutation.isPending}
                        data-testid="button-grant-send-sms"
                      >
                        <Send className="w-3 h-3 mr-1" />
                        Send
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => grantPermissionMutation.mutate({ userId: selectedPermUserId, permissionType: 'can_create_campaigns' })}
                        disabled={!selectedPermUserId || grantPermissionMutation.isPending}
                        data-testid="button-grant-create-campaigns"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Campaigns
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="invites" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Connection Invites</CardTitle>
                <CardDescription>
                  Generate secure links for users to connect their own SMS provider credentials
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Existing Invites */}
                {loadingInvites ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : invites.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No active invites. Create one below to let users connect their SMS credentials.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {invites.map((invite) => {
                      const isExpired = new Date(invite.expiresAt) < new Date();
                      return (
                        <div key={invite.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Link2 className="w-4 h-4 text-muted-foreground" />
                            <div>
                              <p className="font-medium text-sm flex items-center gap-2">
                                {invite.targetUserId ? getUserName(invite.targetUserId) : invite.targetEmail || "Anyone"}
                                {invite.usedAt && (
                                  <Badge className="text-xs bg-green-500/10 text-green-600">Used</Badge>
                                )}
                                {isExpired && !invite.usedAt && (
                                  <Badge variant="destructive" className="text-xs">Expired</Badge>
                                )}
                              </p>
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Expires: {new Date(invite.expiresAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!invite.usedAt && !isExpired && (
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => copyInviteLink(invite.token)}
                                data-testid={`button-copy-invite-${invite.id}`}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              onClick={() => revokeInviteMutation.mutate(invite.id)}
                              disabled={revokeInviteMutation.isPending}
                              data-testid={`button-revoke-invite-${invite.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <Separator />

                {/* Create New Invite */}
                <div className="space-y-3">
                  <Label>Create New Invite</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={selectedUserId} onValueChange={(v) => { setSelectedUserId(v); setInviteEmail(""); }}>
                      <SelectTrigger data-testid="select-invite-user">
                        <SelectValue placeholder="For specific user (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="anyone">Anyone with link</SelectItem>
                        {users.filter(u => u.profile?.role !== 'pending').map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || user.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={expiryDays} onValueChange={setExpiryDays}>
                      <SelectTrigger data-testid="select-invite-expiry">
                        <SelectValue placeholder="Expiry" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 day</SelectItem>
                        <SelectItem value="7">7 days</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={() => createInviteMutation.mutate({
                      userId: selectedUserId && selectedUserId !== "anyone" ? selectedUserId : undefined,
                      email: inviteEmail || undefined,
                      expiresInDays: parseInt(expiryDays),
                    })}
                    disabled={createInviteMutation.isPending}
                    className="w-full"
                    data-testid="button-create-invite"
                  >
                    {createInviteMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Invite Link
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="office-hours" className="space-y-4 mt-4">
            <OfficeHoursPanel />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

